// One shared <video> element for everything that plays with sound (the suite's preview, the overlay, the library's
// full-screen player). Browsers only let a page start media with sound from a tap, so the Talk tap (and the send
// button) primes this element with a tiny silent clip and resumes the AudioContext; after that the app can play a
// clip the Director cued seconds later. Players borrow the element by mounting it into their own box (StageHost), so
// there's never a second element that would need its own tap.
import { primeBeeps } from "./beep";

const PRIME = "/media/prime.mp4";

let el: HTMLVideoElement | null = null;
let unlocked = false;
/** counts playUrl calls: an unlock that resolves after a real play must not pause it (T4: the review screen keeps
 *  the version loaded before the first tap, so the unlock plays that, and a tap's play follows straight away) */
let plays = 0;

export function stageVideo(): HTMLVideoElement {
  if (el) return el;
  el = document.createElement("video");
  el.className = "stage-video";
  el.playsInline = true;
  el.setAttribute("playsinline", "");
  el.setAttribute("webkit-playsinline", "");
  el.preload = "auto";
  return el;
}

/** Call inside a user gesture (the Talk tap, send, a play tap): unlocks media for later, cued playback. */
export function unlockMedia() {
  primeBeeps();
  if (unlocked || typeof document === "undefined") return;
  const v = stageVideo();
  if (!v.paused) return;
  const keep = v.currentSrc && !v.currentSrc.endsWith(PRIME) ? v.src : "";
  v.muted = true;
  if (!keep) v.src = PRIME;
  const seq = plays;
  const p = v.play();
  if (p)
    p.then(() => {
      unlocked = true;
      if (plays !== seq) return; // a real play started meanwhile: leave it playing
      v.pause();
      v.muted = false;
      if (!keep) {
        v.removeAttribute("src");
        v.load();
      }
    }).catch(() => {
      v.muted = false;
    });
}
export const mediaUnlocked = () => unlocked;

/** Put `url` on the element ahead of playing it (a cued clip starts faster). */
export function loadUrl(url: string) {
  const v = stageVideo();
  if (v.getAttribute("src") !== url) {
    v.src = url;
    v.load();
  }
}

/** Play `url` with sound from the start. Rejects when the browser blocks it (no tap yet). */
export function playUrl(url: string): Promise<void> {
  plays++;
  const v = stageVideo();
  loadUrl(url);
  v.muted = false;
  try {
    v.currentTime = 0;
  } catch {
    /* not seekable yet */
  }
  return v.play() ?? Promise.resolve();
}

export function pauseStage() {
  el?.pause();
}

/** Listen to the element (playing / ended / error); returns an unsubscribe. */
export function onStage(type: "playing" | "ended" | "error" | "pause" | "timeupdate", f: () => void): () => void {
  const v = stageVideo();
  v.addEventListener(type, f);
  return () => v.removeEventListener(type, f);
}
